import Button from "@components/Button";
import Icon from "@components/Icon";
import AppModal from "@components/Modal";
import ModalBody from "@components/Modal/ModalBody";
import ModalFooter from "@components/Modal/ModalFooter";
import EmptyState from "@components/EmptyState";
import { INVOICE_LIST, SIGNUP } from "@constants/internal-route.constant";
import { useAuth } from "@context/Auth";
import ConsolePage from "@layout/console/ConsolePage";
import { ModuleService } from "@services/api/Module.service";
import { ToastService } from "@services/utils/toast.service";
import { topProgress } from "@services/utils/topProgress.service";
import { ENV } from "config/ENV.config";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appIcon } from "utility/appIcons";

// One normalised card, whether the source is the public pricing feed (logged-out)
// or the authed store feed (logged-in, which also carries subscription state).
type PriceCard = {
  id: string;
  title: string;
  code: string;
  description?: string;
  unitLabel: string | null;
  unitRate: number | null;
  currency: string | null;
  isSubscribed?: boolean; // authed-only
  meterable?: boolean;
  currentUsage?: number | null;
  currentEstimate?: number | null;
};

// PAYG rates are stored to 4 decimals in public.module_usage_rate (e.g. 0.0115/voucher) — a
// 2-decimal render turned that into "USD 0.01", i.e. a price that isn't the one in the DB.
// Keep 2 decimals for whole/round amounts, expand up to 4 so the stored rate shows verbatim.
const money = (currency: string | null, amount: number | null) =>
  `${currency || "USD"} ${Number(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;

export default function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [cards, setCards] = useState<PriceCard[]>([]);
  const [confirm, setConfirm] = useState<{
    mod: PriceCard;
    action: "subscribe" | "cancel";
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = () => {
    topProgress.show();
    // Logged-in users get subscription state + usage; visitors get public pricing only.
    const req = isAuthenticated ? ModuleService.getStore() : ModuleService.getPublicPricing();
    req
      .then((res) => {
        const rows: any[] = res?.body || [];
        setCards(
          rows.map((m) => ({
            id: m.id,
            title: m.title,
            code: m.code,
            description: m.description,
            unitLabel: m.unitLabel,
            unitRate: m.unitRate,
            currency: m.currency,
            isSubscribed: m.isSubscribed,
            meterable: m.meterable,
            currentUsage: m.currentUsage,
            currentEstimate: m.currentEstimate,
          })),
        );
      })
      .catch((err) => ToastService.error(err?.msg))
      .finally(() => topProgress.hide());
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const doConfirm = () => {
    if (!confirm) return;
    const { mod, action } = confirm;
    setIsSaving(true);
    const req = action === "subscribe" ? ModuleService.subscribe(mod.id) : ModuleService.cancel(mod.id);
    req
      .then(() => {
        ToastService.success(action === "subscribe" ? `Subscribed to ${mod.title}` : `Cancelled ${mod.title}`);
        setConfirm(null);
        load();
      })
      .catch((err) => ToastService.error(err?.msg || "Action failed"))
      .finally(() => setIsSaving(false));
  };

  const grid = (
    <>
      {cards?.length ? (
        <div className="row row-cols-1 row-cols-md-2 row-cols-xxl-3 g-3">
          {cards.map((mod) => (
            <div className="col" key={mod.id}>
              <div className="card h-100">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-start justify-content-between mb-3">
                    <span className="erp-app-icon">
                      <Icon icon={appIcon(mod.code)} size={20} />
                    </span>
                    {isAuthenticated ? (
                      mod.isSubscribed ? (
                        <span className="badge rounded-pill text-bg-success">Active</span>
                      ) : (
                        <span className="badge rounded-pill text-bg-light border">Available</span>
                      )
                    ) : null}
                  </div>

                  <h4 className="mb-1">{mod.title}</h4>
                  <div className="mb-3">
                    <code className="small">{mod.code}</code>
                  </div>

                  {mod.description ? (
                    <p className="small text-body-secondary mb-2">{mod.description}</p>
                  ) : null}

                  <p className="mb-1">
                    <b>{money(mod.currency, mod.unitRate)}</b>{" "}
                    <span className="text-body-secondary">per {mod.unitLabel || "unit"} / month</span>
                  </p>

                  {isAuthenticated && mod.isSubscribed ? (
                    mod.meterable ? (
                      <p className="small text-body-secondary mb-4">
                        <small>
                          This month: <b>{mod.currentUsage ?? 0}</b> {mod.unitLabel}
                          {(mod.currentUsage ?? 0) === 1 ? "" : "s"} ≈{" "}
                          <b>{money(mod.currency, mod.currentEstimate ?? 0)}</b>
                        </small>
                      </p>
                    ) : (
                      <p className="small text-body-secondary mb-4">
                        <small>Billed on usage — metering coming soon.</small>
                      </p>
                    )
                  ) : (
                    <p className="small text-body-secondary mb-4">
                      <small>No fixed plan — invoiced monthly for the usage you accrue.</small>
                    </p>
                  )}

                  <div className="mt-auto">
                    {!isAuthenticated ? (
                      ENV.signupEnabled ? (
                        <Button color="primary" className="w-100" onClick={() => navigate(SIGNUP)}>
                          <Icon icon="rocket-takeoff" size={18} /> &nbsp;30-Days Trial
                        </Button>
                      ) : null
                    ) : mod.isSubscribed ? (
                      <Button
                        color="danger"
                        variant="outline"
                        className="w-100"
                        onClick={() => setConfirm({ mod, action: "cancel" })}
                      >
                        <Icon icon="x-circle" size={18} /> &nbsp;Cancel subscription
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        className="w-100"
                        onClick={() => setConfirm({ mod, action: "subscribe" })}
                      >
                        <Icon icon="cart-plus" size={18} /> &nbsp;Subscribe
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon="grid-3x3-gap" title="No apps available" />
      )}

      <AppModal
        title={confirm?.action === "cancel" ? "Cancel subscription" : "Subscribe"}
        isOpen={!!confirm}
        handleClose={() => setConfirm(null)}
        size="sm"
      >
        <ModalBody>
          {confirm?.action === "cancel" ? (
            <p className="mb-0">
              Stop <b>{confirm?.mod.title}</b>? You’ll no longer be billed for its usage from next month. Existing
              invoices remain payable.
            </p>
          ) : (
            <p className="mb-0">
              Subscribe to <b>{confirm?.mod.title}</b> at{" "}
              <b>
                {confirm ? money(confirm.mod.currency, confirm.mod.unitRate) : ""} per {confirm?.mod.unitLabel} / month
              </b>
              . There’s no upfront charge — you’ll get a monthly invoice for the usage you accrue.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="outline" color="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              color={confirm?.action === "cancel" ? "danger" : "primary"}
              type="submit"
              onClick={doConfirm}
              isLoading={isSaving}
              loadingText="Processing ..."
            >
              {confirm?.action === "cancel" ? "Confirm cancel" : "Confirm subscribe"}
            </Button>
          </div>
        </ModalFooter>
      </AppModal>
    </>
  );

  // Signed in, this is the console's app store; signed out, the public pricing page (PublicLayout).
  if (isAuthenticated)
    return (
      <ConsolePage
        title="Modules Pricing"
        subtitle="Pay only for what you use — each app is billed monthly on usage, with no fixed plan or term."
        crumbs={[{ label: "Modules Pricing" }]}
      >
        <div className="card">
          <div className="card-header d-flex flex-wrap align-items-center gap-2">
            <h3 className="card-title mb-0">Modules &amp; pricing</h3>
            <div className="ms-auto">
              <Button variant="outline" color="secondary" onClick={() => navigate(INVOICE_LIST)}>
                <Icon icon="receipt" /> Invoices
              </Button>
            </div>
          </div>
          <div className="card-body">{grid}</div>
        </div>
      </ConsolePage>
    );

  return (
    <>
      <div className="mb-4">
        <h1 className="h3 mb-1">Pricing</h1>
        <p className="text-body-secondary mb-0">
          Pay only for what you use — each app is billed monthly on usage, with no fixed plan or term.
        </p>
      </div>
      {grid}
    </>
  );
}
